import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import GridLayout from 'react-grid-layout';
import { useTranslation } from 'react-i18next';
import 'react-grid-layout/css/styles.css';
import { useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { useCustomModuleState } from '../Context/index';
import { ElementTemplate } from './TemplateComponent/ElementTemplate';
import { fetchColorArray, matchRule, cellFillColours } from '../../../../Components/helper';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import * as action from '../Store/CustomModule.action';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';

function A4Page({ pageId, pageGrid, pageIndex, isGrdiVisible }, ref) {
  const { moduleLibId } = useParams();
  const {
    CustomModuleReducer: { customModuleDependentControls },
  } = useSelector((reducer) => reducer);
  const [activeZIndex, setActiveZIndex] = useState([]);
  const [deleteImage, setDeleteImage] = useState({
    deleteModal: false,
    key: '',
    index: -1,
  });
  const [localPageState, setLocalPageState] = useState(pageGrid || []);
  // Derived layout for react-grid-layout (pure layout, no control)
  const layoutMemo = useMemo(
    () => localPageState.map(({ x, y, w, h, i, minW, minH, static: isStatic }) => ({ x, y, w, h, i, minW, minH, static: isStatic })),
    [localPageState],
  );
  // Handler to sync layout changes from react-grid-layout
  const handleLayoutChange = useCallback((layoutArr) => {
    // Merge new layout (x, y, w, h, etc) into localPageState, preserving control
    setLocalPageState((prevState) =>
      prevState.map((item) => {
        const layoutItem = layoutArr.find((l) => l.i === item.i);
        return layoutItem ? { ...item, ...layoutItem } : item;
      }),
    );
  }, []);

  const { rowHeight, customModuleState, setCustomModuleState } = useCustomModuleState();
  const { controlPanelOpen } = customModuleState;
  const [droppedIndex, setDroppedIndex] = useState(-1);
  // Calculate the width and height of each grid item
  const dispatch = useDispatch();

  const { t } = useTranslation('common');

  const overflowHideControls = ['HTMLControl', 'Text_Area'];

  async function initialApiCall() {
    await dispatch(action.getDependentPagesList(moduleLibId));
  }

  function handleContentArr(relation, controlValue) {
    const isContent = relation?.isContentArr?.find((item) => item?.isContent?.name === controlValue?.name);
    if (isContent?.to) {
      return isContent?.to;
    }
    return '';
  }

  useEffect(() => {
    if (customModuleDependentControls?.length > 0) {
      const localPage = cloneDeep(localPageState);
      const dependentControl = (localPageState || [])?.filter((item) => item?.control?.key === 'Dependent');
      dependentControl?.forEach((item) => {
        const { relation } = item?.control || { relation: null };
        const currentNewValue = customModuleDependentControls?.find((ele) => ele?.i === relation?.dropDownControl?.i)?.control;
        if (currentNewValue?.value) {
          relation.value = relation.isFixed || relation.fixed ? handleContentArr(relation, currentNewValue?.value) : currentNewValue?.value;
          const localIndex = localPage?.findIndex((ele) => ele?.i === item?.i);
          if (localIndex !== -1) {
            localPage[localIndex] = item;
          }
        }
      });
      setLocalPageState(localPage);
    }
  }, [customModuleDependentControls]);

  useEffect(() => {
    setLocalPageState(pageGrid || []);
    if (pageGrid?.find((item) => item?.control?.type === 'Dependent')) {
      initialApiCall();
    }
  }, [pageGrid]);

  useImperativeHandle(ref, () => ({
    getActivePageData() {
      return localPageState;
    },
  }));

  const setLayout = (grid) => {
    setLocalPageState(cloneDeep(grid));
  };

  const mergeWithExistingData = (updatedGrid) => {
    return updatedGrid.map((item) => {
      const control = localPageState.find((grid) => grid.i === item.i)?.control;
      if (control) {
        return { ...item, control };
      }
      return { ...item };
    });
  };
  // Define grid properties
  const gridProps = {
    className: 'layout',
    cols: 10, // number of columns
    rowHeight, // height of each row
    width: 793.7, // total width of the grid (A4 width)
    margin: [0, 0], // no margin
    containerPadding: [1, 1], // no padding
    maxRows: 50, // maximum number of rows*
    isResizable: true, // disable vertical resizing
    isDraggable: !customModuleState.disableDrag, // disable dragging

    // onResizeStop: (layoutUpdated, oldItem, newItem) => {
    //   // Validate positions and dimensions
    //   const originalItem = localPageState.find((item) => item.i === newItem.i);
    //   if (originalItem && (newItem.x !== originalItem.x || newItem.y !== originalItem.y)) {
    //     toasterMsg(t("CUSTOM_LIBRARY.WARNING_COLUMN"), "info");
    //     // Revert `x` and `y` to original values
    //     newItem.x = originalItem.x;
    //     newItem.y = originalItem.y;
    //     newItem.w = originalItem.w; // Optional: revert width
    //     newItem.h = originalItem.h; // Optional: revert height
    //   }

    //   // Merge and update layout data
    //   const updatedData = mergeWithExistingData(layoutUpdated);
    //   setLayout(updatedData);
    // },
    onResizeStop: (layoutUpdated, oldItem, newItem) => {
      // Validate positions and dimensions
      const originalItem = localPageState.find((item) => item.i === newItem.i);
      const getAfterControl = localPageState.filter((ele) => ele.control && ele.y >= originalItem.y && ele.static === false);
      const getLockedControl = localPageState.filter((ele) => ele.control && ele.y > originalItem.y && ele.static === true);
      const sortLockedByY = getLockedControl.sort((a, b) => a.y - b.y);
      if (sortLockedByY.length > 0 && originalItem) {
        getAfterControl.forEach((controlItem) => {
          const latestData = layoutUpdated.find((item) => item.i === controlItem.i);
          if (latestData && latestData.y > sortLockedByY[0].y) {
            toasterMsg(t('CUSTOM_LIBRARY.WARNING_COLUMN'), 'info');

            // Reset all getAfterControl items to their original positions
            layoutUpdated.forEach((item) => {
              if (getAfterControl.some((control) => control.i === item.i)) {
                const originalData = localPageState.find((localItem) => localItem.i === item.i);
                if (originalData) {
                  item.x = originalData.x;
                  item.y = originalData.y;
                  item.w = originalData.w; // Optional: reset width
                  item.h = originalData.h; // Optional: reset height
                }
              }
            });
          }
        });
      }
      // Merge and update layout data
      const updatedData = mergeWithExistingData(layoutUpdated);
      setLayout(updatedData);
    },
    onLayoutChange: (layoutDropData) => {
      const updatedDropData = mergeWithExistingData(layoutDropData);
      setLayout(updatedDropData); // or mergeWithExistingData(layout)
    },
    onDragStop: (layoutDropData) => {
      // Handle drag completion here
      const updatedDropData = mergeWithExistingData(layoutDropData);
      setLayout(updatedDropData);
    },
  };

  const handleDrop = (item, monitor, gridPartIndex) => {
    const layoutClone = cloneDeep(localPageState);

    // check layoutClone[gridPartIndex]) have comntrol key which is not empty
    if (layoutClone[gridPartIndex]?.control) {
      toasterMsg(t('CUSTOM_MODULE.GRID_OCCUPIED'), 'danger');
    } else {
      setDroppedIndex(gridPartIndex);
      // add item in control key

      layoutClone[gridPartIndex].control = item;
      layoutClone[gridPartIndex].minH = item?.h || 2;
      layoutClone[gridPartIndex].minW = item?.w || 2;
      layoutClone[gridPartIndex].h = item?.h || 2;
      layoutClone[gridPartIndex].w = item?.w || 2;
      setLocalPageState(layoutClone);
    }
  };

  const onChangeCustom = useCallback((value, label, gridIndex) => {
    setLocalPageState((prevState) => {
      const newState = [...prevState];
      const currentGrid = { ...newState[gridIndex] };

      const control = { ...currentGrid.control };

      if (control?.key !== 'Multi_Dropdown' && control?.key !== 'Dropdown' && control?.options?.length > 0) {
        const updatedOptions = control.options.map((e) => {
          if (e.name === label) {
            return { ...e, value };
          }
          if (control.key === 'Ratio_Button') {
            return { ...e, value: false };
          }
          return e;
        });
        control.options = updatedOptions;
      } else {
        control.value = value;
      }

      if (control.key === 'Number_Percentage' || control.key === 'Number_Decimal') {
        if (!control?.colourArray) {
          control.colourArray = [
            {
              colourValue: '',
              colourType: 'green',
              startPercentage: '',
              endPercentage: '',
            },
            {
              colourValue: '',
              colourType: 'amber',
              startPercentage: '',
              endPercentage: '',
            },
            {
              colourValue: '',
              colourType: 'red',
              startPercentage: '',
              endPercentage: '',
            },
          ];
        }
        const newArray = fetchColorArray(control.colourArray);

        const matchingRule = newArray?.find((rule) => matchRule(rule, value));
        if (matchingRule) {
          const valueColour = matchingRule.colourType;
          control.textColour = valueColour;
        } else {
          control.textColour = null;
        }
      }
      if (control.key === 'Status') {
        control.statusColours.forEach((color, colorIndex) => {
          if (color?.colourName === value?.colourName) {
            control.statusColours[colorIndex] = { ...color, setDefault: true };
          } else {
            control.statusColours[colorIndex] = { ...color, setDefault: false };
          }
        });
      }
      if (control.key === 'Text_Area') {
        control.value = value.value;
      }
      if (control.key === 'HTMLControl') {
        control.ckeditorData = value;
      }
      if (control?.key === 'Source') {
        if (label === 'relation') {
          control.label = value.label;
          control.relation = value.relation;
          control.value = null;
        }
        setDroppedIndex(-1);
      }
      if (control.key === 'Calculation') {
        if (label === 'relation') {
          control.label = value.label;
          control.relation = value.relation;
          control.value = null;
        }
        setDroppedIndex(-1);
      }
      if (control.key === 'Dependent') {
        if (label === 'relation') {
          if (!value?.relation?.fixed) {
            const newRelationArray = fetchColorArray(control.colourArray);
            const matchingRule = newRelationArray?.find((rule) => matchRule(rule, value?.relation?.dropDownControl?.control?.value));
            if (matchingRule) {
              const valueColour = cellFillColours(matchingRule?.colourType);
              control.textColour = valueColour;
            }
          }
          control.label = value.label;
          control.relation = value.relation;
          control.value = value?.relation?.dropDownControl?.control?.value || '';
        }
        setDroppedIndex(-1);
      }

      currentGrid.control = control;
      newState[gridIndex] = currentGrid;

      const dependentControls = [];
      const pageControls = localPageState?.filter((grid) => grid?.control);
      pageControls?.forEach((ele) => {
        if (ele?.control?.key === 'Dependent') {
          dependentControls.push(ele);
        }
      });

      if (dependentControls?.length > 0) {
        // Now update dependent controls only if needed
        prevState.forEach((ele, idx) => {
          if (ele?.control?.key === 'Dependent' && ele?.control?.relation?.dropDownControl?.i === currentGrid?.i) {
            const updatedControl = { ...ele.control };
            if (updatedControl.relation?.fixed || updatedControl.relation?.isFixed) {
              updatedControl.relation.value = handleContentArr(updatedControl.relation, value);
            } else {
              updatedControl.relation.value = typeof value === 'object' ? value.value : value;
            }
            newState[idx] = { ...ele, control: updatedControl };
          }
        });
      }

      return newState;
    });

    setDroppedIndex(-1);
  }, []);

  const setParentGridZIndex = (isOpen, gridIndex) => {
    if (isOpen) {
      setActiveZIndex([...activeZIndex, gridIndex]);
    } else {
      setActiveZIndex(activeZIndex.filter((index) => index !== gridIndex));
    }
  };

  const deleteItem = (gridIndex) => {
    const layoutClone = cloneDeep(localPageState);
    const { key, imageUrl } = layoutClone[gridIndex].control;
    if (key === 'Image') {
      setDeleteImage((oldData) => ({
        ...oldData,
        deleteModal: true,
        key: imageUrl,
        index: gridIndex,
      }));
    } else {
      layoutClone[gridIndex].control = null;
      layoutClone[gridIndex].h = 1;
      layoutClone[gridIndex].w = 1;
      layoutClone[gridIndex].minH = 1;
      layoutClone[gridIndex].minW = 1;
      layoutClone[gridIndex].static = false;
      setLocalPageState(layoutClone);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteImage((oldData) => ({
      ...oldData,
      deleteModal: false,
      key: '',
      index: -1,
    }));
  };

  const confirmDelete = async () => {
    const deleteObj = {
      s3Key: deleteImage.key.split('?')[0],
    };
    await dispatch(action.deleteCMImage(deleteObj, t));
    const layoutClone = cloneDeep(localPageState);
    layoutClone[deleteImage.index].control = null;
    layoutClone[deleteImage.index].h = 1;
    layoutClone[deleteImage.index].w = 1;
    layoutClone[deleteImage.index].minH = 1;
    layoutClone[deleteImage.index].minW = 1;
    layoutClone[deleteImage.index].static = false;
    setLocalPageState(layoutClone);
    handleCloseDeleteModal();
  };

  const handleSupplierPermission = (gridIndex) => {
    // optimized code to toggle supplierVisible
    const layoutClone = cloneDeep(localPageState);
    // fetch grid item where i = gridIndex
    layoutClone[gridIndex].control.supplierVisible = !layoutClone[gridIndex].control.supplierVisible;
    setLocalPageState(layoutClone);
  };

  const duplicatingItem = (gridIndex) => {
    // duplicate the grid item and add it to the next grid
    const layoutClone = cloneDeep(localPageState);

    const clonedItem = cloneDeep(layoutClone[gridIndex]); // Deep clone the item

    const requiredX = [];
    const endX = clonedItem.x + clonedItem.w;
    for (let i = clonedItem.x; i < endX; i++) {
      requiredX.push(Number(i));
    }

    const requiredY = [];
    const endY = clonedItem.y + clonedItem.h;
    for (let i = clonedItem.y + 1; i <= endY; i++) {
      // start from clonedItem.y + 1 to exclude the original item's y value
      requiredY.push(Number(i));
    }

    const foundItem = layoutClone.find((item) => item.x === clonedItem.x && item.y === endY); // Increase the height of the original item

    if (foundItem?.control) {
      toasterMsg(t('CUSTOM_MODULE.CONTROL_PLACED'), 'danger');
    } else {
      layoutClone.forEach((item) => {
        if (requiredX.includes(item.x) && requiredY.includes(item.y)) {
          item.y += clonedItem.h;
        }
      });

      const newObj = {
        ...clonedItem,
        i: layoutClone.length.toString(),
        y: clonedItem.y + clonedItem.h,
        static: false,
        control: { ...clonedItem.control, isStatic: false },
      };

      layoutClone.push(newObj);

      setLocalPageState(layoutClone);
    }
  };

  const handleStaticGrid = (gridIndex) => {
    const layoutClone = cloneDeep(localPageState);
    // fetch grid item where i = gridIndex
    const currentStaticVale = layoutClone[gridIndex].control.isStatic;
    layoutClone[gridIndex].control.isStatic = !currentStaticVale;
    layoutClone[gridIndex].static = !currentStaticVale;
    setLocalPageState(layoutClone);
  };

  const updateTableRow = (data, gridIndex, rowInd) => {
    const layoutClone = cloneDeep(localPageState);
    layoutClone[gridIndex].control.tableData[rowInd] = data;
    setLocalPageState(layoutClone);
  };

  const updateTableColumData = (data, gridIndex) => {
    const layoutClone = cloneDeep(localPageState);
    layoutClone[gridIndex].control.tableColumns = data;
    setLocalPageState(layoutClone);
  };

  const updateTableRowData = (rowData, gridIndex, isEmptyColumn) => {
    const layoutClone = cloneDeep(localPageState);

    if (isEmptyColumn) layoutClone[gridIndex].control.tableData[0] = rowData;
    else {
      layoutClone[gridIndex].control.tableData = rowData;
    }
    setLocalPageState(layoutClone);
  };

  const handleTableTitleUpdate = (value, gridIndex) => {
    const layoutClone = cloneDeep(localPageState);
    layoutClone[gridIndex].control.labelInModal = value;
    setLocalPageState(layoutClone);
  };
  const editingInput = (gridIndex) => {
    const clonedRightColumn = cloneDeep(localPageState);
    const editPopupChildValue = clonedRightColumn?.[gridIndex]?.control;
    setCustomModuleState((oldState) => ({
      ...oldState,
      editPopup: true,
      editpopupChildData: editPopupChildValue,
      editPopupPageIndex: pageIndex,
      editPopupGridIndex: gridIndex,
      editPopupPageId: pageId,
    }));
  };

  const handleHeightChange = (gridIndex, newHeight) => {
    const convertedHeight = Math.ceil(newHeight / rowHeight);
    setLocalPageState((prevState) => {
      const updatedState = [...prevState];
      if (updatedState[gridIndex]) {
        updatedState[gridIndex].h = convertedHeight < 2 ? 2 : convertedHeight; // Assuming 'h' is the height property
      }
      return updatedState;
    });
  };
  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="a4-grid-layout">
          {/*
  To ensure react-grid-layout receives a pure layout array, derive it from localPageState.
  The layout prop should only include x, y, w, h, i, minW, minH, static, etc. (no 'control').
  Use useMemo for performance.
*/}
          <GridLayout className="layout" layout={layoutMemo} {...gridProps} onLayoutChange={handleLayoutChange}>
            {localPageState?.map((item, index) => (
              <div key={item.i} className={activeZIndex.includes(index) && 'z-index-99'} style={isGrdiVisible && { border: '1px solid #EEE' }}>
                {controlPanelOpen ? (
                  <GridPart
                    t={t}
                    dataGrid={item}
                    index={index}
                    editingInput={editingInput}
                    deleteItem={deleteItem}
                    handleSupplierPermission={handleSupplierPermission}
                    pageIndex={pageIndex}
                    onDrop={handleDrop}
                    duplicatingItem={duplicatingItem}
                    onChangeCustom={onChangeCustom}
                    droppedIndex={droppedIndex}
                    setDroppedIndex={setDroppedIndex}
                    setParentGridZIndex={setParentGridZIndex}
                    customModuleState={customModuleState}
                    overflowHideControls={overflowHideControls}
                    handleStaticGrid={handleStaticGrid}
                    updateTableRow={updateTableRow}
                    updateTableColumData={updateTableColumData}
                    updateTableRowData={updateTableRowData}
                    handleTableTitleUpdate={handleTableTitleUpdate}
                    localPageState={localPageState}
                    rowHeight={rowHeight}
                    mergeWithExistingData={mergeWithExistingData}
                    setLayout={setLayout}
                    onHeightChange={(newHeight) => handleHeightChange(index, newHeight)}
                  />
                ) : (
                  !controlPanelOpen &&
                  item?.control && (
                    <GridPart
                      t={t}
                      dataGrid={item}
                      index={index}
                      editingInput={editingInput}
                      deleteItem={deleteItem}
                      handleSupplierPermission={handleSupplierPermission}
                      pageIndex={pageIndex}
                      onDrop={handleDrop}
                      duplicatingItem={duplicatingItem}
                      onChangeCustom={onChangeCustom}
                      droppedIndex={droppedIndex}
                      setDroppedIndex={setDroppedIndex}
                      setParentGridZIndex={setParentGridZIndex}
                      customModuleState={customModuleState}
                      overflowHideControls={overflowHideControls}
                      handleStaticGrid={handleStaticGrid}
                      updateTableRow={updateTableRow}
                      updateTableColumData={updateTableColumData}
                      updateTableRowData={updateTableRowData}
                      handleTableTitleUpdate={handleTableTitleUpdate}
                      localPageState={localPageState}
                      rowHeight={rowHeight}
                      mergeWithExistingData={mergeWithExistingData}
                      setLayout={setLayout}
                      onHeightChange={(newHeight) => handleHeightChange(index, newHeight)}
                    />
                  )
                )}
              </div>
            ))}
          </GridLayout>
        </div>
      </DndProvider>
      {deleteImage.deleteModal && (
        <ConfirmationPopup
          onOpen={deleteImage.deleteModal}
          onConfirm={() => confirmDelete()}
          title={t('CUSTOM_MODULE.DELETE_IMAGE')}
          message={t('CUSTOM_MODULE.DELETE_IMAGE_MESSAGE')}
          toggle={() => handleCloseDeleteModal()}
        />
      )}
    </>
  );
}
const A4GridLayout = forwardRef(A4Page);
export default React.memo(A4GridLayout);

function GridPart({
  t,
  dataGrid,
  index,
  onDrop,
  pageIndex,
  editingInput,
  deleteItem,
  handleSupplierPermission,
  duplicatingItem,
  onChangeCustom,
  droppedIndex,
  setDroppedIndex,
  setParentGridZIndex,
  customModuleState,
  overflowHideControls,
  handleStaticGrid,
  updateTableRow,
  updateTableColumData,
  updateTableRowData,
  handleTableTitleUpdate,
  localPageState,
  rowHeight,
  mergeWithExistingData,
  setLayout,
  onHeightChange,
}) {
  const [{ isOver }, drop] = useDrop({
    accept: 'item',
    drop: (item, monitor) => onDrop(item, monitor, index),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });
  const [isLabel, setIsLabel] = useState(true);
  const [isEditLabel, setIsEditLabel] = useState(true);
  const [isCalculation, setIsCalculation] = useState(false);
  const [isEditCalculation, setIsEditCalculation] = useState(false);
  const [isDependent, setIsDependent] = useState(false);
  const [isEditDependent, setIsEditDependent] = useState(false);

  useEffect(() => {
    if (droppedIndex !== -1 && droppedIndex === index && dataGrid?.control) {
      if (dataGrid?.control?.key === t('CUSTOM_LIBRARY.STATIC_CONTROLS.DEPENDENT')) {
        setIsDependent(true);
      } else if (dataGrid?.control?.key === 'Calculation') {
        setIsCalculation(true);
      } else {
        setIsLabel(false);
      }
    }
    // Do not add Index as dependency it is causing error in modal opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedIndex, dataGrid?.control?.key]);
  useEffect(() => {
    if (customModuleState?.modalEdit && customModuleState?.editPopupGridIndex === index && customModuleState?.editPopupGridIndex !== -1 && dataGrid?.control) {
      if (dataGrid?.control?.key === t('CUSTOM_LIBRARY.STATIC_CONTROLS.DEPENDENT')) {
        setIsEditDependent(true);
      } else if (dataGrid?.control?.key === 'Calculation') {
        setIsEditCalculation(true);
      } else {
        setIsEditLabel(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customModuleState?.modalEdit, customModuleState?.editPopupGridIndex, dataGrid?.control?.key]);

  const [onElement, setOnElement] = useState(false);
  const mouseOver = () => {
    setOnElement(true);
  };
  const mouseOut = () => {
    setOnElement(false);
  };

  const handleFixedRelation = (rowData, columnData) => {
    const splitIndex = columnData?.relation?.dropDownControl?.i.split('_');
    const columnIndex = splitIndex[0];
    const columnValue = rowData?.[columnIndex]?.value || '';
    if (columnValue) {
      if (columnData?.relation?.isContentArr?.length > 0) {
        const isContent = columnData?.relation?.isContentArr.find((item) => item?.isContent?.value === columnValue?.value) || null;
        columnData.relation.value = isContent ? isContent?.to || '' : '';
      } else {
        columnData.relation.value = '';
      }
    }
  };

  const handleDropDownControl = (rowData, columnData) => {
    const splitI = columnData?.relation?.dropDownControl?.i.split('_');
    const columnIndex = splitI[0];
    const finalValue = rowData?.[columnIndex]?.value;
    if (columnData?.relation?.colourArray && finalValue) {
      const newArray = fetchColorArray(columnData?.relation?.colourArray || []);
      const matchingRule = newArray?.find((rule) => matchRule(rule, finalValue));
      columnData.relation.cellClass = cellFillColours(matchingRule?.colourType);
      columnData.relation.value = finalValue;
    } else {
      delete columnData?.relation.cellClass;
      columnData.relation.value = '';
    }
  };

  const processDependentColumn = (rowData, columnData) => {
    if (columnData?.relation?.fixed) {
      handleFixedRelation(rowData, columnData);
    } else if (columnData?.relation?.dropDownControl) {
      handleDropDownControl(rowData, columnData);
    }
  };

  const updateTableData = (tableData) => {
    const clonedTableData = cloneDeep(tableData);
    clonedTableData.forEach((rowData) => {
      const rowHasDependent = rowData?.find((cell) => cell?.key === 'Dependent');
      if (rowHasDependent) {
        rowData.forEach((columnData) => {
          if (columnData?.key === 'Dependent') {
            processDependentColumn(rowData, columnData);
          }
        });
      }
    });
    return clonedTableData;
  };

  useEffect(() => {
    if (dataGrid?.control?.key === 'Table' && dataGrid?.control?.tableData?.length > 0) {
      dataGrid.control.tableData = updateTableData(dataGrid.control.tableData);
    }
  }, []);

  return (
    <div
      ref={drop}
      style={{
        height: '100%',
        padding: '0 2px',
        overflow: overflowHideControls.includes(dataGrid?.control?.key) && 'hidden',
      }}
      className={dataGrid?.i}
      onMouseEnter={mouseOver}
      onMouseLeave={mouseOut}
      onKeyDown={mouseOver}
      onKeyUp={mouseOut}
    >
      {isOver && <div className="custom-library-drag-placeholder" />}
      {dataGrid.control ? (
        <ElementTemplate
          {...dataGrid.control}
          keys={dataGrid?.control?.key}
          editingInput={editingInput}
          gridIndex={index}
          pageIndex={pageIndex}
          showDelete
          index={dataGrid?.i}
          gridY={dataGrid?.y}
          gridH={dataGrid?.h}
          gridW={dataGrid?.w}
          fromForm
          deleteItem={deleteItem}
          handleSupplierPermission={handleSupplierPermission}
          duplicatingItem={duplicatingItem}
          onChange={onChangeCustom}
          isLabel={isLabel}
          setIsLabel={setIsLabel}
          setOnElement={setOnElement}
          onElement={onElement}
          setParentGridZIndex={setParentGridZIndex}
          isEditLabel={isEditLabel}
          setIsEditLabel={setIsEditLabel}
          setDroppedIndex={setDroppedIndex}
          isCalculation={isCalculation}
          setIsCalculation={setIsCalculation}
          isEditCalculation={isEditCalculation}
          setIsEditCalculation={setIsEditCalculation}
          handleStaticGrid={handleStaticGrid}
          isDependent={isDependent}
          setIsDependent={setIsDependent}
          isEditDependent={isEditDependent}
          setIsEditDependent={setIsEditDependent}
          updateTableRow={updateTableRow}
          updateTableColumData={updateTableColumData}
          updateTableRowData={updateTableRowData}
          handleTableTitleUpdate={handleTableTitleUpdate}
          localPageState={localPageState}
          rowHeight={rowHeight}
          mergeWithExistingData={mergeWithExistingData}
          setLayout={setLayout}
          onHeightChange={onHeightChange}
        />
      ) : null}
    </div>
  );
}
